import { Button, Modal, Text } from "@marketplace/ui";
import { notifications } from "@marketplace/ui/notifications";
import { useQueryClient } from "@tanstack/react-query";
import { useAtomValue } from "jotai";
import { useTranslation } from "react-i18next";

import { deleteProductModalAtom } from "../../atoms/product-list-page-atom";
import { ProductQueryKeys } from "../../generators/product-query-keys";
import { useDeleteProductDraftMutation } from "../../hooks/mutations/use-delete-product-draft-mutation";
import { useDeleteProductMutation } from "../../hooks/mutations/use-delete-product-mutation";

type DeleteProductModalProps = {
  opened: boolean;
  onClose: () => void;
};

export const DeleteProductModal = ({
  opened,
  onClose
}: DeleteProductModalProps) => {
  const { t: tProduct } = useTranslation("product");
  const { t: tCommon } = useTranslation("common");
  const { data } = useAtomValue(deleteProductModalAtom);
  const queryClient = useQueryClient();

  const { mutateAsync: mutateDeleteProduct, isPending: isDeletingPublished } =
    useDeleteProductMutation();
  const { mutateAsync: mutateDeleteProductDraft, isPending: isDeletingDraft } =
    useDeleteProductDraftMutation({
      options: {
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            queryKey: ProductQueryKeys.getProductDraftList._def
          });
        }
      }
    });

  const handleDelete = async () => {
    try {
      if (!data?.id) return;

      if (data.key) {
        await mutateDeleteProductDraft({ productId: data.key });
      } else {
        await mutateDeleteProduct({ productId: data.id });
      }

      notifications.show({
        message: tProduct("notifications.successDeleteProduct")
      });
      onClose();
    } catch {
      const isDraft = !!data?.key;
      notifications.show({
        variant: "error",
        message: tProduct(
          isDraft
            ? "notifications.failedToDeleteProductDraft"
            : "notifications.faieldToDeleteProduct"
        )
      });
    }
  };

  return (
    <Modal.Root opened={opened} onClose={onClose} size={334}>
      <Modal.Overlay />
      <Modal.Content>
        <Modal.Body classNames={{ body: "!p-[42px]" }}>
          <Text type="paragraph" className="text-center mb-6">
            {tProduct("modal.deleteProductModal.description", {
              productName: data?.name
            })}
          </Text>
          <div className="flex gap-x-[14px]">
            <Button variant="secondary" fullWidth onClick={onClose}>
              {tCommon("no")}
            </Button>
            <Button
              variant="primary"
              fullWidth
              loading={isDeletingPublished || isDeletingDraft}
              onClick={handleDelete}
            >
              {tCommon("yes")}
            </Button>
          </div>
        </Modal.Body>
      </Modal.Content>
    </Modal.Root>
  );
};
